import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components';
import {
  createWebWidget,
  deleteWebWidget,
  getWebWidgetDemoConfig,
  getWebWidgetSessionSettings,
  listWebWidgets,
  resetWebWidgetSessionSettings,
  updateWebWidget,
  updateWebWidgetSessionSettings,
} from '../services/webWidgetAdminService';
import {
  CreateWebWidgetRequest,
  UpdateWebWidgetSessionSettingsRequest,
  WebWidgetAdmin,
  WebWidgetAdminForm,
  WebWidgetSessionSettingsResponse,
} from '../types/webWidgetAdmin';

const emptyForm: WebWidgetAdminForm = {
  name: '',
  widgetKey: '',
  welcomeTitle: 'Welcome',
  welcomeMessage: 'Ask us anything — our team replies here.',
  brandName: '',
  launcherLabel: 'Chat with us',
  primaryColor: '#4F46E5',
  isEnabled: true,
  requireVisitorEmail: false,
  requireVisitorPhone: false,
  allowedOriginsText: '',
};

const parseOriginsText = (value: string): string[] => {
  return value
    .split(/[\n,]+/)
    .map(origin => origin.trim())
    .filter(Boolean);
};

const formFromWidget = (widget: WebWidgetAdmin): WebWidgetAdminForm => ({
  name: widget.name,
  widgetKey: widget.widgetKey,
  welcomeTitle: widget.welcomeTitle,
  welcomeMessage: widget.welcomeMessage,
  brandName: widget.brandName,
  launcherLabel: widget.launcherLabel,
  primaryColor: widget.primaryColor,
  isEnabled: widget.isEnabled,
  requireVisitorEmail: widget.requireVisitorEmail,
  requireVisitorPhone: widget.requireVisitorPhone,
  allowedOriginsText: widget.allowedOrigins.map(item => item.origin).join('\n'),
});

const formToRequest = (form: WebWidgetAdminForm): CreateWebWidgetRequest => ({
  name: form.name.trim(),
  widgetKey: form.widgetKey.trim(),
  welcomeTitle: form.welcomeTitle.trim(),
  welcomeMessage: form.welcomeMessage.trim(),
  brandName: form.brandName.trim(),
  launcherLabel: form.launcherLabel.trim(),
  primaryColor: form.primaryColor.trim(),
  isEnabled: form.isEnabled,
  requireVisitorEmail: form.requireVisitorEmail,
  requireVisitorPhone: form.requireVisitorPhone,
  allowedOrigins: parseOriginsText(form.allowedOriginsText),
});

const buildEmbedSnippet = (tenantCode: string, widgetKey: string): string => {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, '');
  const widgetBase = `${window.location.origin.replace(/\/$/, '')}/widget`;
  const normalizedTenantCode = tenantCode.trim() || 'YOUR_TENANT_CODE';
  const normalizedWidgetKey = widgetKey.trim() || 'YOUR_WIDGET_KEY';

  return `<link rel="stylesheet" href="${widgetBase}/shahagent-webchat.css">
<script
  src="${widgetBase}/shahagent-webchat.js"
  defer
  data-api-base="${apiBase}"
  data-tenant-code="${normalizedTenantCode}"
  data-widget-key="${normalizedWidgetKey}"
></script>`;
};

interface WebChatConnectorTabProps {
  canManage: boolean;
}

export const WebChatConnectorTab: React.FC<WebChatConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'connectors', 'admin']);
  const { showToast } = useToast();

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [widgets, setWidgets] = React.useState<WebWidgetAdmin[]>([]);
  const [selectedWidgetId, setSelectedWidgetId] = React.useState<string>('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [form, setForm] = React.useState<WebWidgetAdminForm>(emptyForm);
  const [tenantCodeForEmbed, setTenantCodeForEmbed] = React.useState('');
  const [sessionSettings, setSessionSettings] = React.useState<WebWidgetSessionSettingsResponse | null>(null);
  const [sessionSettingsForm, setSessionSettingsForm] = React.useState<UpdateWebWidgetSessionSettingsRequest>({
    lifetimeHours: 24,
    absoluteMaximumLifetimeHours: 168,
    slidingExpiration: true,
  });
  const [sessionSettingsSaving, setSessionSettingsSaving] = React.useState(false);

  const selectedWidget = React.useMemo(
    () => widgets.find(widget => widget.id === selectedWidgetId) ?? null,
    [selectedWidgetId, widgets],
  );

  const embedSnippet = React.useMemo(
    () => buildEmbedSnippet(tenantCodeForEmbed, form.widgetKey),
    [form.widgetKey, tenantCodeForEmbed],
  );

  const hasFormChanges = React.useMemo(() => {
    if (isCreating) {
      return Boolean(form.name.trim() || form.widgetKey.trim());
    }

    if (!selectedWidget) {
      return false;
    }

    const baseline = formFromWidget(selectedWidget);
    return JSON.stringify(baseline) !== JSON.stringify(form);
  }, [form, isCreating, selectedWidget]);

  const loadSessionSettings = React.useCallback(async () => {
    if (!canManage) {
      return;
    }

    try {
      const response = await getWebWidgetSessionSettings();
      setSessionSettings(response);
      setSessionSettingsForm({
        lifetimeHours: response.effective.lifetimeHours,
        absoluteMaximumLifetimeHours: response.effective.absoluteMaximumLifetimeHours,
        slidingExpiration: response.effective.slidingExpiration,
      });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('webchatFailedToLoadSessionSettings'));
    }
  }, [canManage, t]);

  const loadWidgets = React.useCallback(async () => {
    if (!canManage) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const items = await listWebWidgets();
      setWidgets(items);

      if (items.length === 0) {
        setSelectedWidgetId('');
        setIsCreating(true);
        setForm(emptyForm);
        return;
      }

      setIsCreating(false);
      setSelectedWidgetId(prev => (items.some(item => item.id === prev) ? prev : items[0].id));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('webchatFailedToLoadWidgets'));
    } finally {
      setLoading(false);
    }
  }, [canManage, t]);

  React.useEffect(() => {
    void loadWidgets();
    void loadSessionSettings();
  }, [loadSessionSettings, loadWidgets]);

  React.useEffect(() => {
    void getWebWidgetDemoConfig()
      .then(config => {
        if (config.tenantCode) {
          setTenantCodeForEmbed(config.tenantCode);
        }
      })
      .catch(() => {
        // Embed section can still be filled manually.
      });
  }, []);

  React.useEffect(() => {
    if (isCreating) {
      return;
    }

    const widget = widgets.find(item => item.id === selectedWidgetId);
    if (widget) {
      setForm(formFromWidget(widget));
    }
  }, [isCreating, selectedWidgetId, widgets]);

  const selectWidget = (widgetId: string) => {
    const widget = widgets.find(item => item.id === widgetId);
    if (!widget) {
      return;
    }

    setIsCreating(false);
    setSelectedWidgetId(widgetId);
    setForm(formFromWidget(widget));
    setError(null);
    setNotice(null);
  };

  const startCreate = () => {
    setIsCreating(true);
    setSelectedWidgetId('');
    setForm(emptyForm);
    setError(null);
    setNotice(null);
  };

  const saveWidget = async () => {
    if (!form.name.trim() || !form.widgetKey.trim() || !form.brandName.trim()) {
      const message = t('webchatNameKeyAndBrandRequired');
      setError(message);
      showToast(message, 'error');
      return;
    }

    if (parseOriginsText(form.allowedOriginsText).length === 0) {
      const message = t('webchatOriginsRequired');
      setError(message);
      showToast(message, 'error');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const request = formToRequest(form);

      if (isCreating) {
        const created = await createWebWidget(request);
        setWidgets(prev => [created, ...prev]);
        setSelectedWidgetId(created.id);
        setIsCreating(false);
        setForm(formFromWidget(created));
        setNotice(t('webchatWidgetCreated'));
        showToast(t('webchatWidgetCreated'), 'success');
        return;
      }

      if (!selectedWidgetId) {
        return;
      }

      const updated = await updateWebWidget(selectedWidgetId, request);
      setWidgets(prev => prev.map(item => (item.id === updated.id ? updated : item)));
      setForm(formFromWidget(updated));
      setNotice(t('webchatWidgetSaved'));
      showToast(t('webchatWidgetSaved'), 'success');
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : t('webchatFailedToSaveWidget');
      setError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const removeWidget = async () => {
    if (!selectedWidgetId || isCreating) {
      return;
    }

    if (!window.confirm(t('webchatDeleteConfirm'))) {
      return;
    }

    setDeleting(true);
    setError(null);
    setNotice(null);

    try {
      await deleteWebWidget(selectedWidgetId);
      const remaining = widgets.filter(item => item.id !== selectedWidgetId);
      setWidgets(remaining);

      if (remaining.length === 0) {
        setIsCreating(true);
        setSelectedWidgetId('');
        setForm(emptyForm);
      } else {
        setSelectedWidgetId(remaining[0].id);
        setForm(formFromWidget(remaining[0]));
        setIsCreating(false);
      }

      setNotice(t('webchatWidgetDeleted'));
      showToast(t('webchatWidgetDeleted'), 'success');
    } catch (deleteError) {
      const message = deleteError instanceof Error ? deleteError.message : t('webchatFailedToDeleteWidget');
      setError(message);
      showToast(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const copyEmbedSnippet = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      showToast(t('webchatEmbedCopied'), 'success');
    } catch {
      showToast(t('webchatEmbedCopyFailed'), 'error');
    }
  };

  const saveSessionSettings = async () => {
    setSessionSettingsSaving(true);
    setError(null);
    setNotice(null);

    try {
      const response = await updateWebWidgetSessionSettings(sessionSettingsForm);
      setSessionSettings(response);
      setSessionSettingsForm({
        lifetimeHours: response.effective.lifetimeHours,
        absoluteMaximumLifetimeHours: response.effective.absoluteMaximumLifetimeHours,
        slidingExpiration: response.effective.slidingExpiration,
      });
      setNotice(t('webchatSessionSettingsSaved'));
      showToast(t('webchatSessionSettingsSaved'), 'success');
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : t('webchatFailedToSaveSessionSettings');
      setError(message);
      showToast(message, 'error');
    } finally {
      setSessionSettingsSaving(false);
    }
  };

  const restoreSessionSettingsDefaults = async () => {
    setSessionSettingsSaving(true);
    setError(null);
    setNotice(null);

    try {
      const response = await resetWebWidgetSessionSettings();
      setSessionSettings(response);
      setSessionSettingsForm({
        lifetimeHours: response.effective.lifetimeHours,
        absoluteMaximumLifetimeHours: response.effective.absoluteMaximumLifetimeHours,
        slidingExpiration: response.effective.slidingExpiration,
      });
      setNotice(t('webchatSessionSettingsReset'));
      showToast(t('webchatSessionSettingsReset'), 'success');
    } catch (resetError) {
      const message = resetError instanceof Error ? resetError.message : t('webchatFailedToSaveSessionSettings');
      setError(message);
      showToast(message, 'error');
    } finally {
      setSessionSettingsSaving(false);
    }
  };

  return (
    <section className="company-section">
      <div className="company-section-head">
        <h3>{t('webchatSettingsHeading')}</h3>
      </div>

      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('webchatSettingsDescription')}
      </p>

      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('webchatLoadingWidgets')} />
        </div>
      ) : (
        <>
          <div className="company-actions-inline" style={{ marginBottom: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={startCreate}>
              {t('webchatCreateWidget')}
            </button>
            {widgets.length > 0 ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{t('webchatSelectWidget')}</span>
                <select
                  className="text-input"
                  value={isCreating ? '' : selectedWidgetId}
                  onChange={event => selectWidget(event.target.value)}
                  disabled={isCreating}
                >
                  <option value="" disabled>{t('webchatSelectWidgetPlaceholder')}</option>
                  {widgets.map(widget => (
                    <option key={widget.id} value={widget.id}>
                      {widget.name} ({widget.widgetKey})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="company-form-grid">
            <label>
              <span>
                {t('webchatWidgetName')}
                <span className="field-required-star" aria-hidden="true">*</span>
              </span>
              <input
                className="text-input"
                value={form.name}
                onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
                placeholder={t('webchatWidgetNamePlaceholder')}
              />
            </label>

            <label>
              <span>
                {t('webchatWidgetKey')}
                <span className="field-required-star" aria-hidden="true">*</span>
              </span>
              <input
                className="text-input"
                value={form.widgetKey}
                onChange={event => setForm(prev => ({ ...prev, widgetKey: event.target.value }))}
                placeholder="simaizeer_marketing"
              />
            </label>

            {!isCreating && selectedWidget?.channelAccountId ? (
              <label>
                {t('webchatChannelAccountId', { ns: 'companies' })}
                <input className="text-input" value={selectedWidget.channelAccountId} readOnly />
              </label>
            ) : null}

            <label>
              {t('webchatWelcomeTitle')}
              <input
                className="text-input"
                value={form.welcomeTitle}
                onChange={event => setForm(prev => ({ ...prev, welcomeTitle: event.target.value }))}
              />
            </label>

            <label>
              <span>
                {t('webchatBrandName')}
                <span className="field-required-star" aria-hidden="true">*</span>
              </span>
              <input
                className="text-input"
                value={form.brandName}
                onChange={event => setForm(prev => ({ ...prev, brandName: event.target.value }))}
                placeholder={t('webchatBrandNamePlaceholder')}
              />
            </label>

            <label>
              {t('webchatLauncherLabel')}
              <input
                className="text-input"
                value={form.launcherLabel}
                onChange={event => setForm(prev => ({ ...prev, launcherLabel: event.target.value }))}
              />
            </label>

            <label>
              {t('webchatPrimaryColor')}
              <input
                className="text-input"
                type="color"
                value={form.primaryColor}
                onChange={event => setForm(prev => ({ ...prev, primaryColor: event.target.value }))}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={event => setForm(prev => ({ ...prev, isEnabled: event.target.checked }))}
              />
              {t('webchatEnabled')}
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={form.requireVisitorEmail}
                onChange={event => setForm(prev => ({ ...prev, requireVisitorEmail: event.target.checked }))}
              />
              {t('webchatRequireVisitorEmail')}
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={form.requireVisitorPhone}
                onChange={event => setForm(prev => ({ ...prev, requireVisitorPhone: event.target.checked }))}
              />
              {t('webchatRequireVisitorPhone')}
            </label>

            <label style={{ gridColumn: '1 / -1' }}>
              {t('webchatWelcomeMessage')}
              <textarea
                className="text-input"
                rows={3}
                value={form.welcomeMessage}
                onChange={event => setForm(prev => ({ ...prev, welcomeMessage: event.target.value }))}
              />
            </label>

            <label style={{ gridColumn: '1 / -1' }}>
              <span>
                {t('webchatAllowedOrigins')}
                <span className="field-required-star" aria-hidden="true">*</span>
              </span>
              <textarea
                className="text-input"
                rows={5}
                value={form.allowedOriginsText}
                onChange={event => setForm(prev => ({ ...prev, allowedOriginsText: event.target.value }))}
                placeholder={'http://localhost:5500\nhttp://localhost:3000\nhttps://www.example.com'}
              />
            </label>
          </div>

          <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
            <div>{t('webchatAllowedOriginsHint')}</div>
            {selectedWidget ? (
              <div>{t('webchatLastUpdated', { value: new Date(selectedWidget.updatedAtUtc).toLocaleString() })}</div>
            ) : null}
          </div>

          <div className="company-actions-inline" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void saveWidget()}
              disabled={saving || !hasFormChanges}
            >
              {saving ? t('savingEllipsis', { ns: 'admin' }) : (isCreating ? t('webchatCreateWidget') : t('webchatSaveWidget'))}
            </button>
            {!isCreating && selectedWidgetId ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void removeWidget()}
                disabled={deleting}
              >
                {deleting ? t('savingEllipsis', { ns: 'admin' }) : t('webchatDeleteWidget')}
              </button>
            ) : null}
          </div>

          <div className="company-section" style={{ marginTop: 24 }}>
            <div className="company-section-head">
              <h3>{t('webchatSessionSettingsHeading')}</h3>
            </div>

            <p style={{ color: '#516173', marginTop: 0 }}>{t('webchatSessionSettingsDescription')}</p>

            {sessionSettings ? (
              <p style={{ color: '#516173', marginTop: 0, fontSize: 13 }}>
                {t('webchatSessionSettingsDefaultsHint', {
                  lifetimeHours: sessionSettings.defaults.lifetimeHours,
                  absoluteMaximumLifetimeHours: sessionSettings.defaults.absoluteMaximumLifetimeHours,
                })}
                {sessionSettings.hasOverrides ? ` ${t('webchatSessionSettingsUsingTenantOverrides')}` : ''}
              </p>
            ) : null}

            <div className="company-form-grid">
              <label>
                {t('webchatSessionLifetimeHours')}
                <input
                  className="text-input"
                  type="number"
                  min={1}
                  max={168}
                  value={sessionSettingsForm.lifetimeHours}
                  onChange={event => setSessionSettingsForm(prev => ({
                    ...prev,
                    lifetimeHours: Number(event.target.value),
                  }))}
                />
              </label>

              <label>
                {t('webchatSessionAbsoluteMaximumLifetimeHours')}
                <input
                  className="text-input"
                  type="number"
                  min={1}
                  max={720}
                  value={sessionSettingsForm.absoluteMaximumLifetimeHours}
                  onChange={event => setSessionSettingsForm(prev => ({
                    ...prev,
                    absoluteMaximumLifetimeHours: Number(event.target.value),
                  }))}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={sessionSettingsForm.slidingExpiration}
                  onChange={event => setSessionSettingsForm(prev => ({
                    ...prev,
                    slidingExpiration: event.target.checked,
                  }))}
                />
                {t('webchatSessionSlidingExpiration')}
              </label>
            </div>

            <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
              <div>{t('webchatSessionSettingsHint')}</div>
            </div>

            <div className="company-actions-inline" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void saveSessionSettings()}
                disabled={sessionSettingsSaving}
              >
                {sessionSettingsSaving ? t('savingEllipsis', { ns: 'admin' }) : t('webchatSaveSessionSettings')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void restoreSessionSettingsDefaults()}
                disabled={sessionSettingsSaving || !sessionSettings?.hasOverrides}
              >
                {t('webchatResetSessionSettings')}
              </button>
            </div>
          </div>

          <div className="company-section" style={{ marginTop: 24 }}>
            <div className="company-section-head">
              <h3>{t('webchatEmbedSection')}</h3>
            </div>

            <p style={{ color: '#516173', marginTop: 0 }}>{t('webchatEmbedDescription')}</p>

            <label>
              {t('webchatTenantCodeForEmbed')}
              <input
                className="text-input"
                value={tenantCodeForEmbed}
                onChange={event => setTenantCodeForEmbed(event.target.value)}
                placeholder="simaizeer"
              />
            </label>

            <pre
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: '#f4f7fa',
                overflowX: 'auto',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {embedSnippet}
            </pre>

            <div className="company-actions-inline" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => void copyEmbedSnippet()}>
                {t('webchatCopyEmbed')}
              </button>
              <Link to="/web-widget-demo" className="btn btn-secondary">
                {t('webchatOpenDemo')}
              </Link>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default WebChatConnectorTab;
